import { contentChild, Directive, inject, input, TemplateRef } from '@angular/core';

export type ScColumnType = 'string' | 'number' | 'date' | 'datetime' | 'boolean';

/** Contexto de `<ng-template scCell let-row let-value="value">`. */
/** La fila llega sin tipo, como en las plantillas de DevExtreme (`let-row`). */
export interface ScCellContext<T = any> {
  $implicit: T;
  value: unknown;
}

/** Un grupo, como lo recibe `<ng-template scGroupCell let-group>` (DX: `value`, `count`). */
export interface ScGroupInfo {
  /** Sin tipo, como en las plantillas de DevExtreme. */
  key: any;
  value: any;
  count: number;
  /** «Caption: valor», el texto de DX sin plantilla. */
  text: string;
}

/** Contexto de `<ng-template scGroupCell let-group>`. */
export interface ScGroupCellContext {
  $implicit: ScGroupInfo;
}

/** Contexto de `<ng-template scDetail let-row>`. */
export interface ScDetailContext<T = any> {
  $implicit: T;
}

/** Plantilla de las celdas de una columna (reemplaza `cellTemplate` de `dxi-column`). */
@Directive({ selector: 'ng-template[scCell]' })
export class ScCellDirective<T = any> {
  readonly template = inject<TemplateRef<ScCellContext<T>>>(TemplateRef);
  static ngTemplateContextGuard<T>(_: ScCellDirective<T>, ctx: unknown): ctx is ScCellContext<T> {
    return true;
  }
}

/** Encabezado de los grupos de una columna (reemplaza `groupCellTemplate`). */
@Directive({ selector: 'ng-template[scGroupCell]' })
export class ScGroupCellDirective {
  readonly template = inject<TemplateRef<ScGroupCellContext>>(TemplateRef);
  static ngTemplateContextGuard(_: ScGroupCellDirective, ctx: unknown): ctx is ScGroupCellContext {
    return true;
  }
}

/** Fila de detalle (reemplaza `dxo-master-detail`): se abre con la flecha de la primera columna. */
@Directive({ selector: 'ng-template[scDetail]' })
export class ScDetailDirective<T = any> {
  readonly template = inject<TemplateRef<ScDetailContext<T>>>(TemplateRef);
  static ngTemplateContextGuard<T>(
    _: ScDetailDirective<T>,
    ctx: unknown,
  ): ctx is ScDetailContext<T> {
    return true;
  }
}

/**
 * Una columna de `sc-table`, declarada como `<dxi-column>`. Sin `field` es calculada (`value`): se
 * ve y se busca, pero no se ordena (como en DevExtreme).
 */
@Directive({ selector: 'sc-column' })
export class ScColumnDirective {
  readonly field = input<string>();
  readonly caption = input.required<string>();
  readonly dataType = input<ScColumnType>('string');
  /** Formato de las fechas: `dd/MM/yyyy HH:mm` o `dd/MM/yyyy`. */
  readonly format = input<string>();
  readonly alignment = input<'left' | 'center' | 'right'>();
  /** Valor calculado de la fila (sólo en modo local). */
  readonly value = input<(row: any) => unknown>();
  /** Texto que se muestra y se busca (reemplaza `customizeText`). */
  readonly displayText = input<(value: unknown, row: any) => string>();
  readonly allowSorting = input(true);
  readonly allowSearch = input(true);
  /** Con el filtro de encabezado de la tabla encendido: si esta columna lo ofrece. */
  readonly allowHeaderFiltering = input(true);
  readonly visible = input(true);
  readonly minWidth = input<number>();
  /** Orden inicial. */
  readonly sortOrder = input<'asc' | 'desc'>();

  /** Agrupada desde el comienzo, en este nivel (como `groupIndex`; sólo en modo local). */
  readonly groupIndex = input<number>();
  /** Se puede agrupar por ella (panel, menú o arrastre). */
  readonly allowGrouping = input(true);

  readonly cell = contentChild(ScCellDirective);
  readonly group = contentChild(ScGroupCellDirective);
}
